// ═══════════════════════════════════════════════════════════════
// RECEIPT TOKEN REPLACEMENT UTILITY
// Path: src/utils/receiptTokens.js
//
// Replaces {tokenName} placeholders in admin-authored receipt
// text with dynamic values from the gift, member, and chapter.
// Unrecognized tokens pass through unchanged.
// ═══════════════════════════════════════════════════════════════

import { formatCurrency } from "../constants/giftTypes";

/**
 * Replace {token} placeholders in a template string.
 * @param {string} template — the admin-authored text (or default)
 * @param {object} context — key/value map of token names to values
 * @returns {string} — template with tokens replaced
 */
export const replaceTokens = (template, context) => {
  if (!template) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return context[key] !== undefined ? String(context[key]) : match;
  });
};

/**
 * Build the standard token context object from gift, member,
 * and chapterSettings. Used by GiftReceiptTemplate and
 * GiftReceiptModal to resolve tokens in custom receipt text.
 */
export const buildReceiptContext = (gift, member, chapterSettings) => {
  const cs = chapterSettings || {};
  const donorFirst = member?.firstName || "Friend";
  const donorType = member?.donorType || "individual";

  // Donor-type-aware full name resolution
  let donorFull;
  switch (donorType) {
    case "household":
      donorFull =
        member?.householdName ||
        ((member?.firstName || "") + " " + (member?.lastName || "")).trim() ||
        "Valued Donor";
      break;
    case "organization":
      donorFull = member?.organization || "Valued Donor";
      break;
    default: // individual
      donorFull =
        member?.credentialedFullName ||
        ((member?.firstName || "") + " " + (member?.lastName || "")).trim() ||
        "Valued Donor";
  }

  const fmtDate = (dateStr) => {
    if (!dateStr) return "--";
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return {
    donorFirstName: donorFirst,
    donorFullName: donorFull,
    householdName: member?.householdName || "",
    donorType: donorType,
    chapterName: cs.legalName || "our organization",
    dafOrg: gift?.dafSponsoringOrg || "your donor advised fund",
    receiptNumber: gift?.receiptNumber || "your receipt",
    signatureName: cs.signatureName || cs.legalName || "our organization",
    giftDate: fmtDate(gift?.giftDate),
    giftAmount: gift?.amount ? formatCurrency(gift.amount) : "$0.00",
  };
};

// ═══════════════════════════════════════════════════════════════
// Email token context — used by QuickLogModal email templates.
// Takes a giftSummary argument ({ lastGiftAmount, lastGiftDate,
// totalGiving }) so the caller can pass already-loaded stats.
// Tokens fall back to warm, human-safe values if data is missing —
// never leaves a raw {token} visible.
// ═══════════════════════════════════════════════════════════════
export const buildEmailContext = (member, chapterSettings, giftSummary) => {
  const cs = chapterSettings || {};
  const gs = giftSummary || {};
  const donorFirst = member?.firstName || "Friend";
  const donorType = member?.donorType || "individual";

  let donorFull;
  switch (donorType) {
    case "household":
      donorFull =
        member?.householdName ||
        ((member?.firstName || "") + " " + (member?.lastName || "")).trim() ||
        "Friend";
      break;
    case "organization":
      donorFull = member?.organization || "Friend";
      break;
    default:
      donorFull =
        member?.credentialedFullName ||
        ((member?.firstName || "") + " " + (member?.lastName || "")).trim() ||
        "Friend";
  }

  const fmtLongDate = (dateStr) => {
    if (!dateStr) return "a recent day";
    return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return {
    donorFirstName: donorFirst,
    donorFullName: donorFull,
    donorType: donorType,
    chapterName: cs.legalName || "our organization",
    signatureName: cs.signatureName || "your fundraising team",
    signatureTitle: cs.signatureTitle || "",
    lastGiftAmount:
      gs.lastGiftAmount != null && gs.lastGiftAmount > 0
        ? formatCurrency(gs.lastGiftAmount)
        : "your recent gift",
    lastGiftDate: fmtLongDate(gs.lastGiftDate),
    totalGiving:
      gs.totalGiving != null && gs.totalGiving > 0
        ? formatCurrency(gs.totalGiving)
        : "your cumulative support",
  };
};
